import React, { Fragment, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { setEnableMobileMenu } from "../../reducers/ThemeOptions";
import {
  UpgradeNav,
  MainNav,
  ComponentsNav,
  FormsNav,
  WidgetsNav,
  ChartsNav,
} from "./NavItems";
import { useAuth } from "../../auth/AuthContext";
import { isRoleAllowed } from "../../auth/roles";

const SubMenu = ({ item, toggleMobileSidebar }) => {
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
  const location = useLocation();
  const { roleType, isAuthenticated } = useAuth();

  const toggleSubMenu = (e) => {
    if (!item.to || item.content) {
      e.preventDefault();
      e.stopPropagation();
      setIsSubMenuOpen(!isSubMenuOpen);
    } else if (item.to && !item.external) {
      // Only dispatch nav-start if we're actually navigating to a different route
      if (location.pathname !== item.to) {
        try { window.dispatchEvent(new CustomEvent('app:nav-start')); } catch (_) {}
      }
      toggleMobileSidebar();
    }
  };

  const isAllowed = (navItem) => {
    if (!navItem.allowedRoles || navItem.allowedRoles.length === 0) return isAuthenticated;
    if (!isAuthenticated) return false;
    return isRoleAllowed(roleType, navItem.allowedRoles);
  };

  const children = (item.content || []).filter(isAllowed);
  const hasSubmenu = children.length > 0;
  
  // Determine if the parent or any child is active
  const isActive = location.pathname === item.to || 
    (hasSubmenu && children.some(child => child.to === location.pathname));

  const LinkComponent = item.external ? 'a' : Link;
  const linkProps = item.external 
    ? { href: item.to, target: "_blank", rel: "noopener noreferrer" }
    : { to: item.to || "#" };

  return (
    <li className={`metismenu-item ${isActive ? "active" : ""}`} style={{ marginBottom: 8 }}>
      <LinkComponent
        {...linkProps}
        className={`metismenu-link ${isActive ? "active" : ""}`}
        onClick={toggleSubMenu}
      >
        <i className={`metismenu-icon ${item.icon}`} />
        {item.label}
        {hasSubmenu && (
          <i className={`metismenu-state-icon pe-7s-angle-${isSubMenuOpen ? 'up' : 'down'}`} />
        )}
      </LinkComponent>
      {hasSubmenu && (
        <ul className={`metismenu-container ${isSubMenuOpen ? "visible" : ""}`} style={{ marginTop: isSubMenuOpen ? 6 : 0 }}>
          {children.map((child, i) => (
            <li key={i} className="metismenu-item" style={{ marginBottom: isSubMenuOpen ? 6 : 0 }}>
              <Link
                to={child.to}
                className={`metismenu-link ${
                  location.pathname === child.to ? "active" : ""
                }`}
                onClick={(ev) => {
                  // Only dispatch nav-start if we're actually navigating to a different route
                  if (location.pathname !== child.to) {
                    try { window.dispatchEvent(new CustomEvent('app:nav-start')); } catch (_) {}
                  }
                  toggleMobileSidebar();
                }}
              >
                {child.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

const Nav = ({ enableMobileMenu, setEnableMobileMenu }) => {
  const { roleType, isAuthenticated } = useAuth();
  const toggleMobileSidebar = () => {
    if (enableMobileMenu) {
      setEnableMobileMenu(false);
    }
  };

  const isAllowed = (item) => {
    if (!isAuthenticated) return false;
    if (item.allowedRoles && item.allowedRoles.length > 0) {
      return isRoleAllowed(roleType, item.allowedRoles);
    }
    // For End User, hide any item without explicit allowedRoles
    if (roleType === "End User") return false;
    // For other roles, allow items without explicit restrictions
    return true;
  };

  const filterItems = (items) => items.filter(isAllowed);

  const upgrade = useMemo(() => filterItems(UpgradeNav), [roleType, isAuthenticated]);
  const main = useMemo(() => filterItems(MainNav), [roleType, isAuthenticated]);
  const comps = useMemo(() => filterItems(ComponentsNav), [roleType, isAuthenticated]);
  const widgets = useMemo(() => filterItems(WidgetsNav), [roleType, isAuthenticated]);
  const forms = useMemo(() => filterItems(FormsNav), [roleType, isAuthenticated]);
  const charts = useMemo(() => filterItems(ChartsNav), [roleType, isAuthenticated]);

  const renderMenu = (items) => items.map((item, i) => (
    <SubMenu key={i} item={item} toggleMobileSidebar={toggleMobileSidebar} />
  ));

  return (
    <Fragment>
      <div className="vertical-nav-menu" style={{ paddingTop: 12 }}>
        {/* Section headers commented for cleaner look; keep lists rendered */}
        {/** <h5 className="app-sidebar__heading">PRO VERSION</h5> **/}
        {/* <ul className="metismenu-container">{renderMenu(upgrade)}</ul> */}

        {/** <h5 className="app-sidebar__heading">MENU</h5> **/}
        <ul className="metismenu-container">{renderMenu(main)}</ul>

        {/** <h5 className="app-sidebar__heading">UI COMPONENTS</h5> **/}
        <ul className="metismenu-container">{renderMenu(comps)}</ul>

        {/** <h5 className="app-sidebar__heading">DASHBOARD WIDGETS</h5> **/}
        <ul className="metismenu-container">{renderMenu(widgets)}</ul>

        {/** <h5 className="app-sidebar__heading">FORMS</h5> **/}
        <ul className="metismenu-container">{renderMenu(forms)}</ul>

        {/** <h5 className="app-sidebar__heading">CHARTS</h5> **/}
        <ul className="metismenu-container">{renderMenu(charts)}</ul>
      </div>
    </Fragment>
  );
};

const mapStateToProps = (state) => ({
  enableMobileMenu: state.ThemeOptions.enableMobileMenu,
});

const mapDispatchToProps = (dispatch) => ({
  setEnableMobileMenu: (enable) => dispatch(setEnableMobileMenu(enable)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Nav);
